/**
 * Policy Upload — the Iron Peak spreadsheet, as a guided flow.
 *
 * WHAT IT REPLACES. Iron Peak members keep an Excel template on the desktop,
 * type a policy into it, save it, and email the file to Cyndy, who re-keys it
 * into AMS360. The uploaded example shows exactly what that costs: the policy
 * number had been typed into the mailing-address cell, so somebody downstream
 * has to notice and fix it.
 *
 * ⚠️ ONE STEP AT A TIME, NOT ONE LONG FORM (Josh, 2026-08-31). It used to show
 * everything at once: bill type, twelve fields, a dropzone at the bottom. That
 * put the dec page — the thing that fills the fields in — AFTER the fields it
 * fills, so the obvious way through the page was the slowest one. Now the order
 * on screen is the order of the work: how it is billed, then the document, then
 * the details it produced, then "any more?", then send.
 *
 * ⚠️ DOCUMENTS BELONG TO A POLICY, NOT TO THE UPLOAD. Two policies with two dec
 * pages used to become one pile of four files, and Cyndy had to work out which
 * belonged to which. Each entry carries its own now, and the email says so.
 *
 * ⚠️ THE FIELD ORDER IS THE SPREADSHEET'S, DELIBERATELY. The people filling this
 * in have muscle memory for that order, and Cyndy reads the result in it. A
 * tidier arrangement would cost more in retraining than it gains.
 *
 * ⚠️ DIRECT BILL IS ASSUMED, NOT ASKED (Josh, 2026-08-31). The form used to open
 * on "how is it billed?", because the two need different paperwork — direct bill
 * wants the dec page, agency bill wants the binder, invoice, proof of payment
 * and PFA. It turns out these are direct bill, so the question was a step
 * between a member and the thing they came to do.
 *
 * The VALUE is still sent and still stored: bill_type keeps its check
 * constraint, the email still says which it is, and the AMS360 wizard still gets
 * an answer. Hiding a question is a decision about what to ask; dropping the
 * field would be a decision about what the data means, and agency bill has not
 * stopped existing. If one turns up, this is one line to bring back.
 *
 * FUTURE: this becomes the input to the AMS360 wizard, which will create the
 * policy directly. Nothing here should assume that — it emails a person today,
 * and the shape of what it sends is what the wizard will read tomorrow.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { PageHero } from '../components/PageHero';
import { StepBar } from '../components/StepBar';
import { ApiError, api } from '../lib/api';
import { Turnstile, turnstileConfigured } from '../components/Turnstile';

type Bill = 'direct' | 'agency';

interface Row {
  policyType: string;
  insuredName: string;
  dba: string;
  mailingAddress: string;
  policyNumber: string;
  effectiveDate: string;
  expirationDate: string;
  lineOfBusiness: string;
  parentCompany: string;
  writingCompany: string;
  premium: string;
  notes: string;
}

interface UploadFile {
  name: string;
  mime: string;
  data: string;
  size: number;
}

/** A policy and the paperwork that came with it. */
interface Entry {
  row: Row;
  files: UploadFile[];
}

const BLANK: Row = {
  policyType: 'Commercial',
  insuredName: '',
  dba: '',
  mailingAddress: '',
  policyNumber: '',
  effectiveDate: '',
  expirationDate: '',
  lineOfBusiness: '',
  parentCompany: '',
  writingCompany: '',
  premium: '',
  notes: '',
};

const newEntry = (): Entry => ({ row: { ...BLANK }, files: [] });

/**
 * The commonest lines, offered as suggestions.
 *
 * ⚠️ A DATALIST, NOT A SELECT. The mock draws line of business as a dropdown,
 * and a dropdown here would be a data-loss bug: readDocuments() writes whatever
 * the dec page actually says into this field, and a fixed list silently drops
 * any value not on it — so the member would send a policy whose line of
 * business is blank or, worse, whichever option happened to sit first. A
 * datalist gives the same one-click picking and still accepts "Garage Keepers
 * Legal Liability" when that is what the paperwork says.
 */
const LINES = [
  'General Liability',
  'Commercial Property',
  'Business Owners (BOP)',
  'Commercial Auto',
  'Workers Compensation',
  'Umbrella / Excess',
  'Professional Liability',
  'Cyber Liability',
  'Inland Marine',
  'Crime',
  'Employment Practices Liability',
];

/** Label, key, and the input type — in the template's own order. */
const FIELDS: {
  key: keyof Row;
  label: string;
  type?: string;
  wide?: boolean;
  required?: boolean;
  placeholder?: string;
  list?: string;
}[] = [
  { key: 'policyType', label: 'Policy type', type: 'select', required: true },
  { key: 'insuredName', label: 'Insured name', required: true, placeholder: 'Enter insured name' },
  { key: 'dba', label: 'DBA (if applicable)', placeholder: 'Doing business as (optional)' },
  { key: 'mailingAddress', label: 'Mailing address', wide: true, required: true, placeholder: 'Street address, city, state, ZIP' },
  { key: 'policyNumber', label: 'Policy number', required: true, placeholder: 'Enter policy number' },
  { key: 'effectiveDate', label: 'Effective date', type: 'date', required: true },
  { key: 'expirationDate', label: 'Expiration date', type: 'date' },
  { key: 'lineOfBusiness', label: 'Line of business', required: true, placeholder: 'Select or type a line of business', list: 'pu-lines' },
  { key: 'parentCompany', label: 'Parent company', placeholder: 'Optional' },
  { key: 'writingCompany', label: 'Writing company', placeholder: 'Optional' },
  { key: 'premium', label: 'Premium', required: true, placeholder: 'e.g. 4,850' },
  { key: 'notes', label: 'Additional notes', wide: true, placeholder: 'Anything the person keying this into AMS360 should know' },
];

/**
 * Where to tell someone to write when this form cannot carry it — an oversized
 * file, or a dead link.
 *
 * ⚠️ ONE CONSTANT, BECAUSE IT KEEPS MOVING. Policy uploads have been addressed
 * to three different mailboxes in a day (syndicates@, which did not exist, then
 * CLHelp@, now onboarding@). Three copies of a literal in three files is three
 * chances for one of them to go stale and send a member somewhere nobody reads.
 *
 * It deliberately does NOT try to mirror POLICY_UPLOAD_EMAIL on the server. The
 * confirmation screen names no address at all for that reason; this is only for
 * "the form could not do it, do it yourself", where a member needs somewhere
 * concrete to write.
 */
export const POLICY_UPLOAD_CONTACT = 'onboarding@agentsalliance.biz';

const DOCS: Record<Bill, string> = {
  direct: 'a copy of the dec page or the policy',
  agency: 'the binder, the invoice, proof of payment, and the PFA if there is one',
};

/** Enough to key a policy. Anything less and it comes straight back to them. */
const isReady = (r: Row): boolean =>
  Boolean(r.insuredName.trim() && r.policyNumber.trim() && r.effectiveDate.trim() && r.premium.trim());

/**
 * Where they are in the flow.
 *
 * `more` is its own stage rather than a pair of buttons under the form, because
 * "are there more?" is a real question with consequences and deserves to be the
 * only thing on screen when it is asked.
 */
type Stage = 'policy' | 'more' | 'review';

/**
 * Two doors to the same form.
 *
 * Josh, 2026-08-31: "duplicate to another separate link so one can remain in
 * the hub and the other can go to members for immediate use." Iron Peak members
 * have no hub accounts yet and waiting for them would leave the spreadsheet in
 * place for weeks.
 *
 * ⚠️ ONE COMPONENT, NOT A COPY. A duplicated page is two pages that drift: the
 * next change to the field list or the dec-page reading would land on one and
 * not the other, and nobody would notice until Agents Alliance received two
 * different-looking emails. The only real difference is where identity comes
 * from — a session, or the sender's own typing — so that is the only thing
 * `linkToken` changes.
 */
export function PolicyUpload({ linkToken }: { linkToken?: string } = {}) {
  const navigate = useNavigate();
  const { email } = useAuth();
  const viaLink = Boolean(linkToken);

  /** Who is sending, when there is no session to ask. */
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [agencyName, setAgencyName] = useState('');
  /**
   * The bot gate on the one unauthenticated write in the app.
   *
   * ⚠️ THE SERVER VERIFIED THIS BEFORE THE PAGE EVER SENT IT. The public route
   * called verifyTurnstile from day one and the form rendered no widget, so with
   * REQUIRE_TURNSTILE on every real submission would have been refused with
   * "could not verify you are a person", and with it off there was no gate at
   * all — while the code read as though there were. A check nothing can satisfy
   * is indistinguishable from no check until you look at both ends.
   */
  const [captcha, setCaptcha] = useState('');
  const senderOk =
    !viaLink ||
    (senderName.trim().length > 1 &&
      agencyName.trim().length > 1 &&
      /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(senderEmail.trim()) &&
      // Only demanded when a site key is actually configured, so a deployment
      // without Turnstile is not silently un-sendable.
      (!turnstileConfigured || Boolean(captcha)));

  const [stage, setStage] = useState<Stage>('policy');

  /**
   * Always direct bill (Josh, 2026-08-31: "let's just only assume they'll be
   * direct bill").
   *
   * ⚠️ STILL SENT, STILL STORED, STILL A TYPE. The question is gone from the
   * screen, not from the record — policy_uploads.bill_type keeps its check
   * constraint, the email keeps saying which it is, and the AMS360 wizard that
   * will read these later still gets an answer. Hard-coding the value is a
   * decision about what to ask a member; deleting the field would be a decision
   * about what the data means, and agency bill has not stopped existing.
   */
  const bill: Bill = 'direct';
  const [entries, setEntries] = useState<Entry[]>([newEntry()]);
  /** Which policy is being worked on. Also how `review` reopens an earlier one. */
  const [at, setAt] = useState(0);

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<{ reference: string; attachments: number; skipped: string[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  /** What the document read filled in, said out loud rather than done silently. */
  const [filled, setFilled] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const entry = entries[at] ?? newEntry();

  const setCell = (key: keyof Row, value: string) =>
    setEntries((es) => es.map((e, n) => (n === at ? { ...e, row: { ...e.row, [key]: value } } : e)));

  /**
   * Fill this policy from its documents.
   *
   * ⚠️ BLANKS ONLY, NEVER OVERWRITES. If somebody has typed a premium and the
   * dec page disagrees, the person wins — they may be looking at an endorsement
   * the PDF predates, and silently replacing their number would be both wrong
   * and invisible. Same reason it reports what it filled rather than filling
   * quietly: this goes to a colleague who keys it into AMS360, so the member has
   * to know which values they are vouching for.
   *
   * ⚠️ EXTRA POLICIES FOUND IN THE FILES BECOME THEIR OWN ENTRIES. Drop two dec
   * pages at once and you get two policies, not one row with the second one's
   * details silently discarded.
   */
  const readDocuments = async (all: UploadFile[], index: number) => {
    setReading(true);
    setFilled(null);
    try {
      const out = await api.policyUploadExtract(
        all.map(({ name, mime, data }) => ({ name, mime, data })),
        linkToken,
      );
      if (!out.policies.length) {
        setFilled(
          out.read === 0
            ? 'I cannot read those file types — they are attached all the same, so just fill the details in below.'
            : 'I could not find a policy in those. Fill the details in below and send as normal.',
        );
        return;
      }

      let count = 0;
      setEntries((current) => {
        const next = [...current];
        out.policies.forEach((p, n) => {
          const target = index + n;
          if (!next[target]) next[target] = newEntry();
          const row = { ...next[target]!.row };
          for (const [k, v] of Object.entries(p)) {
            const key = k as keyof Row;
            if (!v || !(key in BLANK)) continue;
            // policyType has a real default, so "blank" there means "still Commercial".
            const isBlank = key === 'policyType' ? false : !String(row[key] ?? '').trim();
            if (isBlank) {
              row[key] = v;
              count++;
            }
          }
          next[target] = { ...next[target]!, row };
        });
        return next;
      });

      const extra = out.policies.length - 1;
      setFilled(
        (count
          ? `Filled ${count} field${count === 1 ? '' : 's'} from your paperwork. Check them against the document before you send — anything still blank was not on the page.`
          : 'Everything I could read was already filled in.') +
          (extra > 0 ? ` I also found ${extra} more polic${extra === 1 ? 'y' : 'ies'} in there and started ${extra === 1 ? 'it' : 'them'} for you.` : ''),
      );
    } catch (e) {
      // Never fatal. The form still works exactly as it did.
      setFilled(
        e instanceof ApiError
          ? e.message
          : 'Could not read those automatically. Fill the details in below — your files are still attached.',
      );
    } finally {
      setReading(false);
    }
  };

  const addFiles = async (list: FileList | File[] | null) => {
    const incoming = list ? Array.from(list as ArrayLike<File>) : [];
    if (!incoming.length) return;
    setError(null);
    const added: UploadFile[] = [];
    for (const f of incoming) {
      if (f.size > 15 * 1024 * 1024) {
        setError(`${f.name} is over 15 MB — email that one to ${POLICY_UPLOAD_CONTACT} directly.`);
        continue;
      }
      const data = await new Promise<string>((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(String(r.result).split(',')[1] ?? '');
        r.onerror = () => rej(new Error('read failed'));
        r.readAsDataURL(f);
      });
      added.push({ name: f.name, mime: f.type || 'application/octet-stream', data, size: f.size });
    }
    if (!added.length) return;

    const index = at;
    setEntries((es) => es.map((e, n) => (n === index ? { ...e, files: [...e.files, ...added] } : e)));
    // Read straight away rather than behind a button. They dropped a dec page
    // in; asking them to then press "read it" is a step that exists only
    // because the code wanted one.
    void readDocuments(added, index);
  };

  const dropFiles = (i: number, keep: number) =>
    setEntries((es) => es.map((e, n) => (n === i ? { ...e, files: e.files.filter((_, k) => k !== keep) } : e)));

  const allReady = entries.length > 0 && entries.every((e) => isReady(e.row));

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      const rows = entries.map((e) => e.row) as unknown as Record<string, string>[];
      const files = entries.flatMap((e) => e.files);
      const out = linkToken
        ? await api.policyUploadPublic({
            token: linkToken,
            senderName: senderName.trim(),
            senderEmail: senderEmail.trim(),
            agencyName: agencyName.trim(),
            bill,
            rows,
            files,
            turnstileToken: captcha || undefined,
          })
        : // Flattened for the email, which attaches them all. Which policy each
          // belongs to is carried by the ordering — the server lists them in the
          // same order the policies appear.
          await api.policyUpload({ bill, rows, files });
      setSent({ reference: out.reference, attachments: out.attachments, skipped: out.skipped ?? [] });
      setEntries([newEntry()]);
      setAt(0);
      setStage('policy');
      setFilled(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not send it — nothing has gone out.');
    } finally {
      setSending(false);
    }
  };


  if (sent) {
    return (
      <div className="wrap">
        <PageHero title="Sent to Agents Alliance" />
        <div className="panel" style={{ padding: 22, maxWidth: 620 }}>
          <p style={{ fontSize: 14, lineHeight: 1.6 }}>
            {/* ⚠️ THE ADDRESS IS NOT NAMED HERE ANY MORE. It lives in
                POLICY_UPLOAD_EMAIL and can change without a deploy; copy that
                spells it out would quietly start lying the first time it does.
                "A copy is in your inbox" is the line that matters anyway — it
                is checkable by the member, which an address they cannot see
                the far end of is not. */}
            Reference <b>{sent.reference}</b>, with{' '}
            {sent.attachments === 0
              ? 'no documents attached'
              : `${sent.attachments} document${sent.attachments === 1 ? '' : 's'}`}
            . A copy is in your inbox. Someone will enter it into AMS360 — you do not need to send
            the spreadsheet as well.
          </p>
          {/*
            ⚠️ THE MEMBER IS TOLD WHAT DID NOT GO. Oversized files are dropped
            server-side and were named in the email to Agents Alliance but never
            here, so the person who could actually fix it was the only one not
            told. They would have seen a plain success and found out weeks later,
            when someone rang to ask for the dec page.
          */}
          {sent.skipped.length > 0 && (
            <p style={{ fontSize: 14, lineHeight: 1.6, color: '#87212E' }}>
              <b>Too large to send:</b> {sent.skipped.join(', ')}. Those did <b>not</b> go — email
              them to {POLICY_UPLOAD_CONTACT} quoting {sent.reference}.
            </p>
          )}
          <div className="supp-buttons" style={{ marginTop: 16 }}>
            <button className="btn btn-primary" onClick={() => setSent(null)}>
              Send another policy
            </button>
            <button className="btn btn-ghost" onClick={() => navigate('/hub')}>
              Back to the hub
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wrap">
      <PageHero
        title="Policy Upload"
        sub="A policy you have written that Agents Alliance needs to enter into AMS360. This replaces the spreadsheet."
      />

      <div className="pu-col">
      {/*
        Where they are, spelled out. The flow already had three stages and no
        way to see them, so "This policy is done" led somewhere the member had
        no picture of — and a form that hides its own length gets abandoned in
        the middle.
      */}
      {/* ⚠️ NOT CLICKABLE HERE, unlike the Inspector's. These steps are a
          position report: jumping to "Review and send" would skip the checks
          that stop a policy going across without a premium on it. */}
      <StepBar
        steps={[
          { key: 'policy', label: 'Policy' },
          { key: 'more', label: 'Any more?' },
          { key: 'review', label: 'Review and send' },
        ]}
        current={stage}
        label="Where you are"
      />

      {/* ---- 1. The document, then the details it produced ---- */}
      {stage === 'policy' && (
        <>
          <section className="pu-panel">
            <div className="pu-head">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M14 3H7a1 1 0 00-1 1v16a1 1 0 001 1h10a1 1 0 001-1V7zM14 3v4h4M12 17v-6M9.5 13.5L12 11l2.5 2.5" />
              </svg>
              <div>
                <h3>
                  {entries.length > 1 ? `Policy ${at + 1} — upload policy documents` : 'Upload policy documents'}
                </h3>
                <p>
                  Drop {DOCS[bill]} here and I’ll fill the details in underneath. Anything attached
                  here goes to Agents Alliance to enter the policy into AMS360 — PDFs and photos are
                  the ones I can read from.
                </p>
              </div>
            </div>
            {/*
              ⚠️ IT SAID "DROP YOUR DOCUMENTS HERE" AND HAD NO DROP HANDLER
              (reported 2026-08-31). A label wrapping a hidden input handles the
              CLICK for free, which is why it looked finished and passed a
              click-through — but a browser's default for a dropped file is to
              navigate to it, so dropping one did nothing at all.

              dragOver must preventDefault too, or the drop event never fires.
            */}
            <label
              className={`pu-drop${dragging ? ' is-over' : ''}${reading ? ' is-busy' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                void addFiles(e.dataTransfer.files);
              }}
            >
              <input type="file" multiple hidden onChange={(e) => void addFiles(e.target.files)} />
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6.5 18a4.5 4.5 0 01-.6-8.96 6 6 0 0111.64-1.1A4.25 4.25 0 0118 18M12 20v-8M9 15l3-3 3 3" />
              </svg>
              <b>{reading ? 'Reading it…' : dragging ? 'Drop it here' : 'Drag and drop files here'}</b>
              <span>
                {reading ? 'Pulling the policy details off the document.' : 'Or click to browse your files'}
              </span>
              {/* ⚠️ 15 MB, NOT THE MOCK'S 25. The server drops anything larger and
                  names it back to the member; printing a limit the upload cannot
                  actually honour would send people away to find out the hard way. */}
              {!reading && <span className="pu-drop-types">PDF, JPG, PNG up to 15 MB</span>}
            </label>

            {filled && !reading && <p className="supp-note">{filled}</p>}

            {entry.files.length > 0 && (
              <div className="ps-files" style={{ marginTop: 10 }}>
                {entry.files.map((f, i) => (
                  <div key={f.name + i} className="ps-filerow">
                    <span className="ps-file-name">{f.name}</span>
                    <span className="ps-file-size">{Math.max(1, Math.round(f.size / 1024))} KB</span>
                    <button className="ps-file-x" onClick={() => dropFiles(at, i)}>
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            {error && <div className="insured-error">{error}</div>}
          </section>

          <section className="pu-panel">
            <div className="pu-head">
              <div>
                <h3>{entries.length > 1 ? `Policy ${at + 1} — policy details` : 'Policy details'}</h3>
              </div>
            </div>
            <div className="sf-fields">
              {FIELDS.map((f) => (
                <div key={f.key} className={`sf-field${f.wide ? ' is-wide' : ''}`}>
                  <label htmlFor={`${f.key}-${at}`}>
                    {f.label}
                    {f.required && <span className="pu-req"> *</span>}
                  </label>
                  {f.type === 'select' ? (
                    <select
                      id={`${f.key}-${at}`}
                      value={entry.row[f.key]}
                      onChange={(e) => setCell(f.key, e.target.value)}
                    >
                      <option>Commercial</option>
                      <option>Personal</option>
                    </select>
                  ) : (
                    <input
                      id={`${f.key}-${at}`}
                      type={f.type === 'date' ? 'date' : 'text'}
                      list={f.list}
                      placeholder={f.placeholder}
                      value={entry.row[f.key]}
                      onChange={(e) => setCell(f.key, e.target.value)}
                    />
                  )}
                </div>
              ))}
            </div>
            <datalist id="pu-lines">
              {LINES.map((l) => (
                <option key={l} value={l} />
              ))}
            </datalist>
          </section>

          <div className="supp-actions">
            <div className="supp-buttons">
              <button
                className="btn btn-primary"
                disabled={!isReady(entry.row)}
                onClick={() => {
                  setFilled(null);
                  setStage('more');
                }}
              >
                This policy is done
              </button>
            </div>
            <p className="supp-note">
              {isReady(entry.row)
                ? 'You can come back and change any of this before it sends.'
                : 'Insured name, policy number, effective date and premium are needed — without them it comes straight back to you.'}
            </p>
          </div>
        </>
      )}

      {/* ---- 2. Any more? ---- */}
      {stage === 'more' && (
        <section className="pu-panel">
          <div className="pu-head">
            <div>
              <h3>Any other policies to add?</h3>
              <p>
                {entries.length === 1 ? 'One policy so far.' : `${entries.length} policies so far.`}{' '}
                They all go across in the same message, so adding another here saves you sending
                twice.
              </p>
            </div>
          </div>
          <div className="supp-buttons" style={{ marginTop: 18 }}>
            <button
              className="btn btn-primary"
              onClick={() => {
                setEntries((es) => [...es, newEntry()]);
                setAt(entries.length);
                setFilled(null);
                setStage('policy');
              }}
            >
              Yes, add another policy
            </button>
            <button className="btn btn-ghost" onClick={() => setStage('review')}>
              No, that’s everything
            </button>
          </div>
        </section>
      )}

      {/* ---- 3. Review, then send ---- */}
      {stage === 'review' && (
        <>
          <section className="pu-panel">
            <div className="pu-head">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3a9 9 0 100 18 9 9 0 000-18zM8 12l3 3 5-5" />
              </svg>
              <div>
                <h3>Ready to send</h3>
                <p>
                  {entries.length === 1 ? 'One policy' : `${entries.length} policies`}, going to
                  Agents Alliance to be entered into AMS360.
                </p>
              </div>
            </div>
            <div className="ps-files" style={{ marginTop: 18 }}>
              {entries.map((e, i) => (
                <div key={i} className="ps-filerow">
                  <span className="ps-file-name">
                    <b>{e.row.insuredName || 'Unnamed'}</b> — {e.row.policyNumber || 'no policy number'}
                    {e.row.premium ? ` · ${e.row.premium}` : ''}
                    {e.files.length ? ` · ${e.files.length} file${e.files.length === 1 ? '' : 's'}` : ' · no documents'}
                  </span>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setAt(i);
                      setFilled(null);
                      setStage('policy');
                    }}
                  >
                    Edit
                  </button>
                  {entries.length > 1 && (
                    <button
                      className="ps-file-x"
                      title="Remove this policy"
                      onClick={() => {
                        setEntries((es) => es.filter((_, n) => n !== i));
                        setAt(0);
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
            {/* Named rather than assumed: a policy with no paperwork is one Cyndy
                has to chase, and it is cheaper to notice it here. */}
            {entries.some((e) => !e.files.length) && (
              <p className="supp-note">
                One of these has no documents attached. It will still send, but whoever enters it
                will have to come back to you for the paperwork.
              </p>
            )}
          </section>

          {/*
            ⚠️ ASKED HERE, NOT AT THE START. Through the link there is no session
            to read, and Agents Alliance keys these into AMS360 then comes back
            with questions — a policy that arrives from nobody looks actionable
            and is not. It sits at the end rather than the front because the
            first thing anybody should meet is "drop the dec page and watch it
            fill", not a form asking who they are.
          */}
          {viaLink && (
            <section className="pu-panel">
              <div className="pu-head">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 12a4 4 0 100-8 4 4 0 000 8zM4 21a8 8 0 0116 0" />
                </svg>
                <div>
                  <h3>Who is sending this?</h3>
                  <p>So we know whose policy it is, and who to come back to if anything is unclear.</p>
                </div>
              </div>
              <div className="sf-fields">
                <div className="sf-field">
                  <label htmlFor="pu-name">
                    Your name<span className="pu-req"> *</span>
                  </label>
                  <input id="pu-name" type="text" value={senderName} onChange={(e) => setSenderName(e.target.value)} />
                </div>
                <div className="sf-field">
                  <label htmlFor="pu-email">
                    Your email<span className="pu-req"> *</span>
                  </label>
                  <input
                    id="pu-email"
                    type="email"
                    value={senderEmail}
                    onChange={(e) => setSenderEmail(e.target.value)}
                  />
                </div>
                <div className="sf-field is-wide">
                  <label htmlFor="pu-agency">
                    Your agency<span className="pu-req"> *</span>
                  </label>
                  <input
                    id="pu-agency"
                    type="text"
                    value={agencyName}
                    onChange={(e) => setAgencyName(e.target.value)}
                  />
                </div>
              </div>
              <Turnstile onToken={setCaptcha} />
            </section>
          )}

          <div className="supp-actions">
            <div className="supp-buttons">
              <button
                className="btn btn-primary"
                disabled={!allReady || !senderOk || sending}
                onClick={() => void send()}
              >
                {sending ? 'Sending…' : 'Send to Agents Alliance'}
              </button>
              <button className="btn btn-ghost" onClick={() => setStage('more')}>
                Add another policy
              </button>
            </div>
            <p className="supp-note">
              {!allReady
                ? 'One of these is missing an insured name, policy number, effective date or premium — open it and finish it first.'
                : !senderOk
                  ? 'Add your name, email and agency above so we know who this is from.'
                  : `Goes to the Agents Alliance commercial lines desk, copied to ${
                      viaLink ? senderEmail.trim() || 'you' : email ?? 'you'
                    }.`}
            </p>
            {error && <div className="insured-error">{error}</div>}
          </div>
        </>
      )}
      </div>
    </div>
  );
}
